/**
 * 確認の窓（docs/06 4-7）。
 *
 * 消す・無効にするなど**元に戻しにくい操作の前**に必ずはさみます（docs/06 原則4）。
 *  - 何が・いくつ・どうなるかを具体の数で書く
 *  - 元に戻せるかどうかを1行で書く
 *  - 「やめる」が左・実行が右。実行は、いまあるものを消す・止める操作（消す・無効にする・外す・取り下げる）は
 *    危険の見た目（赤）、先へ進める操作（承認する・送る・取り込む・解除する）は主役の見た目
 *    （`tone="primary"`・issue #265 L-5・docs/06 4-7）
 *  - 窓の外を押しただけでは実行しない。`Esc` は「やめる」と同じ
 *  - **キーボードだけでも使い切れる**: 開いたら「やめる」に合わせ、`Tab` は窓の中だけを回り、
 *    閉じたら呼び出し元のボタンへ戻ります（issue #177 の3番）
 *
 * ボタンの並びは「やめる」が左・実行が右です（docs/06 4-7）。
 * 入力のまとまり（`FormCard`）は逆に「保存」が左ですが、これは意図した違いです
 * （進める操作は左から始め、取り返しのつかない操作だけは指から遠ざける）。
 */
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { Button } from './Button.js';

/** 窓の中でキーボードが行き来できるところ。 */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface ConfirmDialogProps {
  /** 開いているか */
  open: boolean;
  /** 窓の見出し（「アカウントを無効化します」） */
  title: string;
  /** 何が・いくつ・どうなるか */
  message: ReactNode;
  /** 元に戻せるかどうか（「元に戻せません」「30日以内なら元に戻せます」） */
  reversibility: string;
  /**
   * 元に戻せる操作か。戻せる操作の「戻せます」は安心材料なので、危険の赤ではなく
   * ふつうの色で出す（赤は「戻せません」だけ・総点検 M12）
   */
  reversible?: boolean;
  /**
   * 実行ボタンの見た目。既定は危険（赤）。先へ進める操作（承認する・送る・取り込む・解除する）は
   * `primary` にして、「完全に消す」「無効にする」と見分けられるようにする（issue #265 L-5・docs/06 4-7）
   */
  tone?: 'danger' | 'primary';
  /** 実行ボタンの言葉（「無効化する」）。動詞で書く */
  confirmLabel: string;
  /** やめるボタンの言葉 */
  cancelLabel?: string;
  /** 実行中（押せなくなる） */
  busy?: boolean;
  /** 実行ボタンだけを押せなくする（窓の中で選んでいただくまで・など） */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  message,
  reversibility,
  reversible = false,
  tone = 'danger',
  confirmLabel,
  cancelLabel = 'やめる',
  busy = false,
  confirmDisabled = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps): ReactNode {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  /** 窓を開く前に選ばれていたところ（閉じたらここへ戻す）。 */
  const openerRef = useRef<HTMLElement | null>(null);
  // 窓を2つ開いても見出しの id がぶつからないようにする（issue #177 の8番）
  const titleId = useId();
  const messageId = useId();

  // 開いたら「やめる」に合わせる＝間違えて実行しにくい側から始める。
  // 閉じたら、呼び出したボタンへ戻す（キーボードだけの方が一覧の先頭に飛ばされない）
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement;
    openerRef.current = opener instanceof HTMLElement ? opener : null;
    cancelRef.current?.focus();
    return () => {
      openerRef.current?.focus();
      openerRef.current = null;
    };
  }, [open]);

  // Esc は「やめる」と同じ。Tab は窓の中だけを回る（背後の画面へ抜けない）
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && !busy) {
        onCancel();
        return;
      }
      if (event.key !== 'Tab') return;
      const root = dialogRef.current;
      if (!root) return;
      const stops = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE));
      const first = stops[0];
      const last = stops[stops.length - 1];
      // 行き先が1つも無いとき（実行中など）は、窓の外へ出さない
      if (!first || !last) {
        event.preventDefault();
        return;
      }
      const active = document.activeElement;
      if (!(active instanceof HTMLElement) || !root.contains(active)) {
        event.preventDefault();
        first.focus();
        return;
      }
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, busy, onCancel]);

  if (!open) return null;

  return (
    <div className="ui-dialog-backdrop">
      <div
        ref={dialogRef}
        className="ui-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={messageId}
      >
        <h2 id={titleId}>{title}</h2>
        <div id={messageId} className="ui-dialog-message">
          <p>{message}</p>
          <p className={`ui-dialog-reversibility${reversible ? ' is-reversible' : ''}`}>
            {reversibility}
          </p>
        </div>
        <div className="ui-dialog-actions">
          <Button ref={cancelRef} variant="secondary" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button variant={tone} onClick={onConfirm} busy={busy} disabled={confirmDisabled}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
